export {};

// app.luwu.dev/team: the signed-in person's team (people, invitations,
// shared computers), and /team/join#<token>, where an invitation link lands.
// The token stays in the fragment (and sessionStorage across sign-in), never
// in a URL a server sees.

import { apiBase } from "./server";

const LUWU = apiBase();
const TOKEN = "luwu-token";
const INVITE = "luwu-invite";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const store = (s: Storage, k: string, v?: string | null) => {
  try {
    if (v === undefined) return s.getItem(k);
    if (v === null) s.removeItem(k);
    else s.setItem(k, v);
  } catch {
    // private mode
  }
  return null;
};
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const day = (s: number | null | undefined) => (s ? new Date(s * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

const token = store(localStorage, TOKEN);

interface Mine {
  team: { id: string; name: string; role: "owner" | "admin" | "member"; owner_account_id: string } | null;
  you: string;
  plan: { name: string; seats: number; ends_at: number; computers: number } | null;
  seats: { total: number; members: number; invites: number };
  members: { account_id: string; role: string; joined_at: number; name: string; email: string | null; computers: number }[];
  invites: { id: string; email: string; role: string; created_at: number; expires_at: number }[];
  /** mode "system": a shared machine (a company server's), with a member map. */
  computers: { id: string; name: string; platform: string; owner_name: string; mine: boolean; online: boolean; mode?: string }[];
  my_computers: { id: string; name: string; platform: string; version: string; shared: boolean }[];
  devices: { id: string; account_id: string; kind: string; name: string; platform: string; last_seen_at: number | null }[];
}

interface Block {
  id: string;
  kind: string;
  value: string;
  reason: string;
  created_at: number;
  expires_at: number | null;
  detail: Record<string, string>;
  who: string | null;
  active: boolean;
}

interface Running {
  sessions: { id: string; name: string; windows: number; account_id?: string; who?: string }[];
  runs: { id: string; agent: string; state: string; account_id?: string; who?: string }[];
  shares: { id: string; window_label: string; guests: number }[];
  reported_at: number | null;
}

function view(v: "loading" | "none" | "join" | "team") {
  for (const id of ["loading", "none", "join", "team"]) $(`v-${id}`).hidden = id !== v;
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${LUWU}${path}`, {
    method,
    headers: { authorization: `Bearer ${token}`, ...(body ? { "content-type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) {
    signIn();
    throw new Error("Sign in first.");
  }
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(data.error ?? `Request failed (${r.status})`);
  return data;
}

function signIn() {
  location.replace(`/account?next=${encodeURIComponent(location.pathname)}`);
}

/** Two clicks for anything that takes someone out: the first says what happens. */
function twoStep(b: HTMLButtonElement, armed: string, run: () => Promise<void>) {
  const idle = b.textContent ?? "";
  b.addEventListener("click", async () => {
    if (!b.classList.contains("armed")) {
      b.classList.add("armed");
      b.textContent = armed;
      setTimeout(() => {
        if (!b.disabled) {
          b.classList.remove("armed");
          b.textContent = idle;
        }
      }, 6000);
      return;
    }
    b.disabled = true;
    try {
      await run();
    } catch (x) {
      $("e-team").textContent = (x as Error).message;
      b.disabled = false;
    }
  });
}

let mine: Mine;

async function load() {
  mine = await api<Mine>("GET", "/v1/orgs/mine");
  if (!mine.team) return view("none");
  draw();
  view("team");
}

function draw() {
  const m = mine;
  const t = m.team!;
  const manage = t.role === "owner" || t.role === "admin";
  $("team-name").textContent = t.name;
  $("team-line").textContent = m.plan
    ? `Team plan until ${day(m.plan.ends_at)} · ${m.seats.members + m.seats.invites} of ${m.plan.seats} seats in use · you're ${t.role === "member" ? "a member" : `the ${t.role}`}`
    : `No Team plan right now, so nothing is shared. ${manage ? "Choose one on the Plan page." : "Its owner can renew it."}`;

  // People.
  const roleCell = (x: Mine["members"][number]) =>
    manage && x.role !== "owner" && x.account_id !== m.you
      ? `<select data-role="${x.account_id}" aria-label="Role of ${esc(x.name)}"><option value="member" ${x.role === "member" ? "selected" : ""}>Member</option><option value="admin" ${x.role === "admin" ? "selected" : ""}>Admin</option></select>`
      : esc(x.role[0]!.toUpperCase() + x.role.slice(1));
  $("members").innerHTML =
    `<tr><th>Name</th><th>Role</th></tr>` +
    m.members
      .map(
        (x) =>
          `<tr><td>${esc(x.name || "Someone")}${x.account_id === m.you ? " (you)" : ""}<span class="sub">${x.email ? `${esc(x.email)} · ` : ""}${x.computers === 1 ? "1 computer" : `${x.computers} computers`}</span></td><td><div class="acts">${roleCell(x)}${manage && x.role !== "owner" && x.account_id !== m.you ? `<button class="btn small danger" type="button" data-remove="${x.account_id}">Remove</button><button class="btn small danger" type="button" data-block-account="${x.account_id}">Block</button>` : ""}</div></td></tr>`,
      )
      .join("");
  for (const s of $("members").querySelectorAll<HTMLSelectElement>("[data-role]")) {
    s.addEventListener("change", async () => {
      $("e-team").textContent = "";
      try {
        await api("PATCH", `/v1/orgs/${t.id}/members/${s.dataset.role}`, { role: s.value });
      } catch (x) {
        $("e-team").textContent = (x as Error).message;
      }
    });
  }
  for (const b of $("members").querySelectorAll<HTMLButtonElement>("[data-remove]")) {
    const who = m.members.find((x) => x.account_id === b.dataset.remove)!;
    twoStep(b, `Remove ${who.name || "them"}`, async () => {
      await api("DELETE", `/v1/orgs/${t.id}/members/${b.dataset.remove}`);
      await load();
    });
  }

  for (const b of $("members").querySelectorAll<HTMLButtonElement>("[data-block-account]")) {
    const who = m.members.find((x) => x.account_id === b.dataset.blockAccount)!;
    twoStep(b, `Block ${who.name || "them"}`, async () => {
      await api("POST", `/v1/orgs/${t.id}/blocks`, { kind: "account", value: b.dataset.blockAccount });
      await load();
    });
  }

  // Invitations.
  $("invite-box").hidden = !manage;
  $("invites").innerHTML = m.invites.length
    ? `<div class="table-wrap" style="margin-top:12px"><table><tr><th>Invited</th><th></th></tr>${m.invites
        .map((i) => `<tr><td>${esc(i.email)}<span class="sub">${i.role === "admin" ? "Admin" : "Member"} · works until ${day(i.expires_at)}</span></td><td class="num"><button class="btn small plain" type="button" data-withdraw="${i.id}">Withdraw</button></td></tr>`)
        .join("")}</table></div>`
    : "";
  for (const b of $("invites").querySelectorAll<HTMLButtonElement>("[data-withdraw]")) {
    b.addEventListener("click", async () => {
      b.disabled = true;
      try {
        await api("DELETE", `/v1/orgs/${t.id}/invites/${b.dataset.withdraw}`);
        await load();
      } catch (x) {
        $("e-invite").textContent = (x as Error).message;
        b.disabled = false;
      }
    });
  }

  // Computers the team shares, then the caller's own.
  $("shared").innerHTML = m.computers.length
    ? `<div class="table-wrap"><table><tr><th>Computer</th><th class="num"></th></tr>${m.computers
        .map(
          (d) =>
            `<tr><td><span class="dot ${d.online ? "on" : ""}" aria-label="${d.online ? "Online" : "Offline"}"></span>${esc(d.name)}<span class="sub">${d.online ? "Online" : "Offline"} · shared by ${d.mine ? "you" : esc(d.owner_name || "a teammate")}</span></td><td class="num">${manage && d.online ? `<button class="btn small plain" type="button" data-running="${d.id}">Running</button> ` : ""}${manage && d.mode === "system" ? `<button class="btn small plain" type="button" data-members="${d.id}">Members</button> ` : ""}${!d.mine && manage ? `<button class="btn small plain" type="button" data-unshare="${d.id}">Stop sharing</button>` : ""}</td></tr>`,
        )
        .join("")}</table></div><p class="fine">Open them from the Luwu app or <a href="/computers">Computers</a>.</p>`
    : `<p>Nothing shared yet.</p>`;
  $("mine").innerHTML = m.my_computers.length
    ? `<div class="table-wrap"><table><tr><th>Computer</th><th class="num"></th></tr>${m.my_computers
        .map(
          (d) =>
            `<tr><td>${esc(d.name)}${d.version ? `<span class="sub">Luwu ${esc(d.version)}</span>` : ""}</td><td class="num"><button class="btn small ${d.shared ? "plain" : "primary"}" type="button" data-${d.shared ? "unshare" : "share"}="${d.id}">${d.shared ? "Stop sharing" : "Share with the team"}</button></td></tr>`,
        )
        .join("")}</table></div>`
    : `<p>You have no computers yet. Add one with <code>luwu up</code>.</p>`;
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-share],[data-unshare]")) {
    b.addEventListener("click", async () => {
      b.disabled = true;
      $("e-team").textContent = "";
      try {
        if (b.dataset.share) await api("POST", `/v1/orgs/${t.id}/computers`, { device_id: b.dataset.share });
        else await api("DELETE", `/v1/orgs/${t.id}/computers/${b.dataset.unshare}`);
        await load();
      } catch (x) {
        $("e-team").textContent = (x as Error).message;
        b.disabled = false;
      }
    });
  }

  for (const b of $("shared").querySelectorAll<HTMLButtonElement>("[data-running]")) b.addEventListener("click", () => showRunning(b));
  for (const b of $("shared").querySelectorAll<HTMLButtonElement>("[data-members]")) b.addEventListener("click", () => showMembers(b));

  // Members' devices and the team's blocks (owner and admins).
  $("admin-box").hidden = !manage;
  if (manage) {
    const names = new Map(m.members.map((x) => [x.account_id, x.name || "Someone"]));
    $("devs").innerHTML = m.devices.length
      ? `<div class="table-wrap"><table><tr><th>Device</th><th class="num"></th></tr>${m.devices
          .map(
            (d) =>
              `<tr><td>${esc(d.name || d.platform)}<span class="sub">${esc(names.get(d.account_id) ?? "")} · ${d.kind === "host" ? "computer" : "phone or browser"}</span></td><td class="num">${d.account_id === m.team!.owner_account_id && d.account_id !== m.you ? "" : `<button class="btn small danger" type="button" data-block-device="${d.id}">Block</button>`}</td></tr>`,
          )
          .join("")}</table></div>`
      : `<p>No devices yet.</p>`;
    for (const b of $("devs").querySelectorAll<HTMLButtonElement>("[data-block-device]")) {
      twoStep(b, "Block it on the team", async () => {
        await api("POST", `/v1/orgs/${t.id}/blocks`, { kind: "device", value: b.dataset.blockDevice });
        await load();
      });
    }
    void drawBlocks(t.id);
  }

  // Leaving, or for the owner, deleting.
  const leave = $<HTMLButtonElement>("leave");
  const fresh = leave.cloneNode(true) as HTMLButtonElement;
  leave.replaceWith(fresh);
  if (t.role === "owner") {
    fresh.textContent = "Delete the team";
    $("leave-note").textContent = m.plan
      ? "The team's plan is paid until " + day(m.plan.ends_at) + ". Cancel it on the Plan page first, or wait until it ends."
      : "Everyone leaves and shared computers go back to their owners. Nothing on the computers is touched.";
    fresh.disabled = !!m.plan;
    twoStep(fresh, `Delete ${t.name}`, async () => {
      await api("DELETE", `/v1/orgs/${t.id}`);
      await load();
    });
  } else {
    fresh.textContent = `Leave ${t.name}`;
    $("leave-note").textContent = "You lose access to the team's computers, and yours stop being shared. Your account goes back to its own plan.";
    twoStep(fresh, `Leave ${t.name}`, async () => {
      await api("DELETE", `/v1/orgs/${t.id}/members/${m.you}`);
      await load();
    });
  }
}

/** The team's active blocks, each with Lift. */
async function drawBlocks(org: string) {
  const box = $("blocks");
  try {
    const { blocks } = await api<{ blocks: Block[] }>("GET", `/v1/orgs/${org}/blocks`);
    const live = blocks.filter((b) => b.active);
    box.innerHTML = live.length
      ? `<div class="table-wrap"><table><tr><th>Blocked</th><th class="num"></th></tr>${live
          .map(
            (b) =>
              `<tr><td>${esc(b.kind === "account" ? (b.who ?? "A person") : `${b.detail.name || "A device"}${b.who ? ` (${b.who})` : ""}`)}<span class="sub">${b.kind === "account" ? "Person" : "Device"} · since ${day(b.created_at)}${b.reason ? ` · ${esc(b.reason)}` : ""}</span></td><td class="num"><button class="btn small plain" type="button" data-lift="${b.id}">Lift</button></td></tr>`,
          )
          .join("")}</table></div>`
      : `<p>Nobody is blocked.</p>`;
    for (const b of box.querySelectorAll<HTMLButtonElement>("[data-lift]")) {
      b.addEventListener("click", async () => {
        b.disabled = true;
        try {
          await api("DELETE", `/v1/orgs/${org}/blocks/${b.dataset.lift}`);
          await load();
        } catch (x) {
          $("e-team").textContent = (x as Error).message;
          b.disabled = false;
        }
      });
    }
  } catch (x) {
    box.innerHTML = `<p class="error">${esc((x as Error).message)}</p>`;
  }
}

/** Under a shared computer: what it's running, each with End. */
async function showRunning(b: HTMLButtonElement) {
  const row = b.closest("tr")!;
  if (row.nextElementSibling?.classList.contains("running")) {
    row.nextElementSibling.remove();
    return;
  }
  b.disabled = true;
  const org = mine.team!.id;
  try {
    const r = await api<Running>("GET", `/v1/orgs/${org}/computers/${b.dataset.running}/running`);
    const items = [
      ...r.sessions.map((s) => ({
        label: `Terminal session ${s.name} (${s.windows} ${s.windows === 1 ? "window" : "windows"})${s.who ? `, ${s.who}'s` : ""}`,
        body: { session_id: s.id, ...(s.account_id ? { account_id: s.account_id } : {}) },
        verb: "End",
      })),
      ...r.runs.map((x) => ({ label: `Agent run: ${x.agent}, ${x.state}${x.who ? `, ${x.who}'s` : ""}`, body: { run_id: x.id, ...(x.account_id ? { account_id: x.account_id } : {}) }, verb: "Stop" })),
      ...r.shares.map((x) => ({ label: `Shared window ${x.window_label || ""} (${x.guests} joined)`, body: { share_id: x.id }, verb: "End" })),
    ];
    const tr = document.createElement("tr");
    tr.className = "running";
    tr.innerHTML = `<td colspan="2">${
      items.length
        ? `<ul>${items.map((it, i) => `<li><span>${esc(it.label)}</span><button class="btn small danger" type="button" data-end="${i}">${it.verb}</button></li>`).join("")}</ul>`
        : r.reported_at
          ? "Nothing running."
          : "This computer hasn't reported what it runs yet. It needs Luwu 0.2.15 or later."
    }</td>`;
    row.after(tr);
    for (const e of tr.querySelectorAll<HTMLButtonElement>("[data-end]")) {
      const it = items[Number(e.dataset.end)]!;
      twoStep(e, `${it.verb} it`, async () => {
        const res = await api<{ effect: string }>("POST", `/v1/orgs/${org}/blocks`, { kind: "session", device_id: b.dataset.running, ...it.body });
        e.closest("li")!.innerHTML = `<span>${esc(res.effect)}</span>`;
      });
    }
  } catch (x) {
    $("e-team").textContent = (x as Error).message;
  } finally {
    b.disabled = false;
  }
}

interface HostUser {
  account_id: string;
  unix: string;
  name?: string;
  email?: string;
  /** "map": set here; "auto": from the server's unix_from rule. */
  source?: string;
}

/** Under a shared machine: who may use it, and as which Unix user. */
async function showMembers(b: HTMLButtonElement) {
  const row = b.closest("tr")!;
  if (row.nextElementSibling?.classList.contains("members")) {
    row.nextElementSibling.remove();
    return;
  }
  b.disabled = true;
  const org = mine.team!.id;
  const device = b.dataset.members!;
  try {
    const r = await api<{ users: HostUser[]; unmapped: HostUser[] }>("GET", `/v1/orgs/${org}/computers/${device}/users`);
    const line = (u: HostUser, mapped: boolean) =>
      `<li><span>${esc(u.name || "Someone")}${u.email ? `<span class="sub">${esc(u.email)}</span>` : ""}</span>` +
      `<span class="inline"><input data-unix="${esc(u.account_id)}" value="${esc(u.unix)}" placeholder="unix user" autocapitalize="off" spellcheck="false" aria-label="Unix user of ${esc(u.name || "this person")}">` +
      `<button class="btn small ${mapped ? "plain" : "primary"}" type="button" data-map="${esc(u.account_id)}">${mapped ? "Save" : "Map"}</button>` +
      `${mapped && u.source === "map" ? `<button class="btn small danger" type="button" data-unmap="${esc(u.account_id)}">Remove</button>` : ""}` +
      `${mapped && u.source === "auto" ? `<span class="sub">from the server's rule</span>` : ""}</span></li>`;
    const tr = document.createElement("tr");
    tr.className = "members";
    tr.innerHTML = `<td colspan="2"><p class="fine">Each person works as their own Unix user on this machine. Someone not listed can't use it.</p><ul>${[
      ...r.users.map((u) => line(u, true)),
      ...r.unmapped.map((u) => line(u, false)),
    ].join("")}</ul><div class="error" data-members-error role="alert"></div></td>`;
    row.after(tr);
    const err = tr.querySelector<HTMLElement>("[data-members-error]")!;
    for (const e of tr.querySelectorAll<HTMLButtonElement>("[data-map]")) {
      e.addEventListener("click", async () => {
        err.textContent = "";
        e.disabled = true;
        try {
          await api("PUT", `/v1/orgs/${org}/computers/${device}/users`, { account_id: e.dataset.map, unix: tr.querySelector<HTMLInputElement>(`[data-unix="${e.dataset.map}"]`)!.value.trim() });
          tr.remove();
          await showMembers(b);
        } catch (x) {
          err.textContent = (x as Error).message;
          e.disabled = false;
        }
      });
    }
    for (const e of tr.querySelectorAll<HTMLButtonElement>("[data-unmap]")) {
      twoStep(e, "Remove", async () => {
        await api("DELETE", `/v1/orgs/${org}/computers/${device}/users/${e.dataset.unmap}`);
        tr.remove();
        await showMembers(b);
      });
    }
  } catch (x) {
    $("e-team").textContent = (x as Error).message;
  } finally {
    b.disabled = false;
  }
}

$<HTMLFormElement>("f-invite").addEventListener("submit", async (e) => {
  e.preventDefault();
  const err = $("e-invite");
  err.textContent = "";
  const email = $<HTMLInputElement>("invite-email").value.trim();
  if (!email.includes("@")) return void (err.textContent = "Enter their email address.");
  try {
    const r = await api<{ link: string; emailed: boolean }>("POST", `/v1/orgs/${mine.team!.id}/invites`, { email, role: $<HTMLSelectElement>("invite-role").value });
    $<HTMLInputElement>("invite-email").value = "";
    $("invite-msg").textContent = r.emailed ? `Sent to ${email}. You can also send them this link:` : `The email didn't go out. Send ${email} this link yourself:`;
    $("invite-link").textContent = r.link;
    $("invite-made").hidden = false;
    await load();
  } catch (x) {
    err.textContent = (x as Error).message;
  }
});
$("invite-copy").addEventListener("click", async () => {
  try {
    await navigator.clipboard.writeText($("invite-link").textContent ?? "");
    $("invite-copy").textContent = "Copied";
  } catch {
    // select it instead
    getSelection()?.selectAllChildren($("invite-link"));
  }
});

/** /team/join#<token>: show the invitation, join on a click. */
async function join() {
  const fromHash = location.hash.slice(1);
  if (/^[0-9a-f]{48}$/.test(fromHash)) {
    store(sessionStorage, INVITE, fromHash);
    history.replaceState(null, "", "/team/join");
  }
  const inv = store(sessionStorage, INVITE);
  if (!inv) {
    view("join");
    $("join-title").textContent = "This invitation link is incomplete";
    $("join-line").textContent = "Open the link from the email again, all of it.";
    $("join").hidden = true;
    return;
  }
  if (!token) return signIn();
  view("join");
  try {
    const p = await api<{ team: string; email: string; role: string }>("POST", "/v1/orgs/invites/peek", { token: inv });
    $("join-title").textContent = `Join ${p.team}`;
    $("join-line").textContent = `This invitation is for ${p.email}, as ${p.role === "admin" ? "an admin" : "a member"}.`;
  } catch (x) {
    $("join-title").textContent = "This invitation can't be used";
    $("join-line").textContent = (x as Error).message;
    $("join").hidden = true;
    store(sessionStorage, INVITE, null);
    return;
  }
  $("join").addEventListener("click", async () => {
    const b = $<HTMLButtonElement>("join");
    b.disabled = true;
    $("e-join").textContent = "";
    try {
      await api("POST", "/v1/orgs/invites/accept", { token: inv });
      store(sessionStorage, INVITE, null);
      history.replaceState(null, "", "/team");
      view("loading");
      await load();
    } catch (x) {
      $("e-join").textContent = (x as Error).message;
      b.disabled = false;
    }
  });
}

async function who() {
  try {
    const me = await api<{ display_name: string | null; username: string | null; email: string | null }>("GET", "/v1/me");
    $("who").textContent = me.display_name || me.username || me.email || "";
  } catch {
    // just no name
  }
}

const joining = location.pathname.replace(/\/$/, "").endsWith("/join");
if (joining) void join();
else if (!token) signIn();
else
  load().catch((x) => {
    $("v-loading").innerHTML = `<p class="error">${esc((x as Error).message)}</p>`;
  });
if (token) void who();

// Sign out of the console: forget the token and go to the sign-in page.
document.getElementById("sign-out")?.addEventListener("click", () => {
  try {
    localStorage.removeItem(TOKEN);
  } catch {
    // storage unavailable
  }
  location.replace("/account");
});
